import { useEffect, useState } from "react";
import { LayoutDashboard, Package, Boxes, ShoppingCart, FileText, LogOut, Lock, BarChart3, Ticket, Star, MessageCircle, Tags, Award } from "lucide-react";
import api, { setAuthFailHandler, apiError } from "./api";
import Dashboard from "./Dashboard";
import Products from "./Products";
import Stock from "./Stock";
import Orders from "./Orders";
import Reports from "./Reports";
import Coupons from "./Coupons";
import Reviews from "./Reviews";
import Content from "./Content";
import Whatsapp from "./Whatsapp";
import Categories from "./Categories";
import Brands from "./Brands";

const tabs = [
  { id: "dashboard", label: "Visão geral", icon: LayoutDashboard },
  { id: "produtos", label: "Produtos", icon: Package },
  { id: "categorias", label: "Categorias", icon: Tags },
  { id: "marcas", label: "Marcas", icon: Award },
  { id: "estoque", label: "Estoque", icon: Boxes },
  { id: "pedidos", label: "Pedidos & Vendas", icon: ShoppingCart },
  { id: "cupons", label: "Cupons", icon: Ticket },
  { id: "avaliacoes", label: "Avaliações", icon: Star },
  { id: "relatorios", label: "Relatórios", icon: BarChart3 },
  { id: "conteudo", label: "Conteúdo", icon: FileText },
  { id: "whatsapp", label: "Integração WhatsApp", icon: MessageCircle },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(apiError(err, "Não foi possível entrar."));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-military-dark flex items-center justify-center px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-offwhite rounded-lg p-10 shadow-2xl">
        <p className="font-serif italic text-3xl text-military-dark mb-1">leve <span className="not-italic font-semibold text-military">essenza</span></p>
        <p className="text-xs uppercase tracking-[0.25em] text-military-dark/50 font-semibold mb-8">Painel administrativo</p>
        <input
          data-testid="admin-email-input"
          type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-white border border-military/20 rounded-lg px-4 py-3 text-sm mb-4 focus:outline-none focus:border-military"
        />
        <input
          data-testid="admin-password-input"
          type="password" required placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-white border border-military/20 rounded-lg px-4 py-3 text-sm mb-4 focus:outline-none focus:border-military"
        />
        {error && <p data-testid="admin-login-error" className="text-sm text-red-700 mb-4">{error}</p>}
        <button
          data-testid="admin-login-button" type="submit" disabled={loading}
          className="w-full rounded-full bg-military text-offwhite py-3.5 text-sm uppercase tracking-widest font-semibold hover:bg-military-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Lock size={14} /> {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
};

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    setAuthFailHandler(() => setUser(null));
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return <div className="min-h-screen bg-military-dark flex items-center justify-center text-offwhite/60 text-sm">Carregando...</div>;
  }
  if (user === null) return <Login onLogin={setUser} />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const views = { dashboard: <Dashboard go={setTab} />, produtos: <Products />, categorias: <Categories />, marcas: <Brands />, estoque: <Stock />, pedidos: <Orders />, cupons: <Coupons />, avaliacoes: <Reviews />, relatorios: <Reports />, conteudo: <Content />, whatsapp: <Whatsapp /> };

  return (
    <div className="min-h-screen bg-offwhite font-sans" data-testid="admin-panel">
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/80 backdrop-blur-xl border-b border-military/10 z-40 flex items-center justify-between px-4 md:px-8">
        <p className="font-serif italic text-xl text-military-dark">leve <span className="not-italic font-semibold text-military">essenza</span> <span className="text-xs not-italic font-sans uppercase tracking-widest text-military-dark/40 ml-2">admin</span></p>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-xs text-military-dark/50">{user.email}</span>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-military-dark/60 hover:text-military-dark transition-colors">
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      <aside className="hidden md:flex flex-col gap-1 fixed top-16 bottom-0 left-0 w-56 bg-white border-r border-military/10 p-4 z-30">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors ${tab === t.id ? "bg-military text-offwhite" : "text-military-dark/60 hover:bg-baby hover:text-military-dark"}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </aside>

      <main className="pt-16 md:pl-56 pb-24 md:pb-10">
        <div className="p-4 md:p-10 max-w-6xl">{views[tab]}</div>
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-military/10 z-40 flex justify-around py-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-mobile-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex flex-col items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-wider font-semibold ${tab === t.id ? "text-military" : "text-military-dark/40"}`}
          >
            <t.icon size={18} /> {t.label.split(" ")[0]}
          </button>
        ))}
      </nav>
    </div>
  );
}
